import { getTopLeft as OLExtentGetTopLeft, getWidth as OLExtentGetWidth } from 'ol/extent'
import OLLayerTile from 'ol/layer/Tile'
import { get as OLProjGet } from 'ol/proj'
import OLSourceWMTS from 'ol/source/WMTS'
import OLTileGridWMTS from 'ol/tilegrid/WMTS'
import TileState from 'ol/TileState'

import type { ConfigLayerWMTSOptions } from './catalog/ConfigLayerWMTS'
import type { ImageTile } from 'ol'
import type { Projection as OLProjection } from 'ol/proj'
import type { TileImage } from 'ol/source'
import type { Options as OLSourceWMTSOptions } from 'ol/source/WMTS'
import type { LoadFunction } from 'ol/Tile'

import * as PoziUtils from 'api/utils'
import type { ConfigLayerBaseTileOptions } from 'config/ConfigLayerBaseTileOptions'
import { MapLayer } from 'config/MapLayer'
import { setLayerLoadDone, setLayerLoadFailed, setLayerLoadInProgress } from 'drawer/tabs/layers/actions'
import { loadImage } from 'utils/Image'

export class MapLayerWMTS extends MapLayer {
  static createLayer (layerConf: ConfigLayerWMTSOptions): OLLayerTile<TileImage> {
    // console.debug('MapLayerWMTS - createLayer', layerConf)

    this.preLayer(layerConf)

    const { titleid } = layerConf

    const sourceOptions = MapLayerWMTS.getSourceOptions(layerConf)

    const olSourceWMTS = new OLSourceWMTS(sourceOptions)

    // Retrieve any potential headers from the config. They can be useful for
    // authentication purposes (e.g. 'authkey' headers)
    const headers: Record<string, string> | undefined = layerConf.config?.proxyHeaders || {}

    // Options to be passed to the XHR request
    const xhrOptions = {
      headers
    }

    let tileLoadingCount = 0
    let atLeastOneTileLoadedSuccessfully = false

    const tileLoadingStart = (titleid: ConfigLayerWMTSOptions['titleid']) => {
      tileLoadingCount++

      // console.debug('tileLoadingStart', titleid, tileLoadingCount)

      window.store.dispatch(setLayerLoadInProgress(titleid))
    }

    const tileLoadingEnd = (titleid: ConfigLayerWMTSOptions['titleid']) => {
      tileLoadingCount--

      // console.debug('tileLoadingEnd', titleid, tileLoadingCount, atLeastOneTileLoadedSuccessfully)

      // Only mark layer as finished (done/failed) when there are no more tiles to load
      if (tileLoadingCount <= 0) {
        // If at least one of the tiles loaded successfully we mark the loading as done
        if (atLeastOneTileLoadedSuccessfully) {
          window.store.dispatch(setLayerLoadDone(titleid))
        } else {
          // Else, we report that the layer loading has failed
          window.store.dispatch(setLayerLoadFailed(titleid))
        }
      }
    }

    // @ts-expect-error // Cannot get typescript to be happy with the following code
    const tileLoadFunction: LoadFunction = async (tile: ImageTile, src: string) => {
      // console.debug('MapLayerWMTS - tileLoadFunction', titleid, src, { ...xhrOptions })

      try {
        tileLoadingStart(titleid)

        const data = await loadImage(src, xhrOptions)
        if (data) {
          // @ts-expect-error
          tile.getImage().src = URL.createObjectURL(data)

          atLeastOneTileLoadedSuccessfully = true
        } else {
          tile.setState(TileState.ERROR)
        }
      } catch (ex) {
        tile.setState(TileState.ERROR)
      } finally {
        tileLoadingEnd(titleid)
      }
    }

    olSourceWMTS.setTileLoadFunction(tileLoadFunction)

    const tileLayerOptions: ConfigLayerBaseTileOptions = {
      title: layerConf.title,
      titleid: layerConf.titleid,
      group: layerConf.group,
      source: olSourceWMTS,
      visible: PoziUtils.isLayerSelected(layerConf, true)
    }

    const olLayer = new OLLayerTile(tileLayerOptions)

    // console.debug('MapLayerWMTS - createLayer - layer', layerConf.titleid, olLayer)

    return olLayer
  }

  static getSourceOptions (layerConf: ConfigLayerWMTSOptions): OLSourceWMTSOptions {
    // console.debug('MapLayerWMTS - getSourceOptions', layerConf)

    const {
      optionsFromCapabilities
    } = layerConf?.config?.wmts || {}

    // If we found an OL generated set of options, that's what we'll use
    if (optionsFromCapabilities) {
      // console.debug('MapLayerWMTS - getOptions - optionsFromCapabilities', optionsFromCapabilities)

      return optionsFromCapabilities
    }

    // Else, try to create options from defaults and user supplied values
    const {
      crossOrigin = 'Anonymous',
      format = 'image/png',
      layer = '',
      matrixSet = 'EPSG:3857',
      projection: projectionLike = 'EPSG:3857',
      style = 'default',
      tileSize = 256,
      url = layerConf?.config?.url
    } = layerConf?.config?.wmts || {}

    const projection = OLProjGet(projectionLike) as OLProjection

    const projectionExtent = projection.getExtent()
    const origin = OLExtentGetTopLeft(projectionExtent)

    const size = OLExtentGetWidth(projectionExtent) / 256
    const resolutions = new Array(20)
    const matrixIds = new Array(20)

    // generate resolutions and matrixIds arrays for this WMTS
    for (let z = 0; z <= 20; ++z) {
      resolutions[z] = size / Math.pow(2, z)
      matrixIds[z] = z
    }

    const options: OLSourceWMTSOptions = {
      crossOrigin,
      format,
      layer,
      matrixSet,
      projection,
      style,
      tileGrid: new OLTileGridWMTS({
        tileSize,
        origin,
        resolutions,
        matrixIds
      }),
      url
    }

    // console.debug('MapLayerWMTS - getSourceOptions - generated', options)

    return options
  }
}
